import React, { ReactNode, useState, useMemo } from 'react'
import { GlobalNotificationStyled } from './GlobalNotification.styled'
import { Warning } from '@clickcease/cc-client-components-lib'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useTranslation } from 'react-i18next'
import { trailDaysLeft } from 'Utils/dates'
import { useMigrationDisplay } from 'Hooks/useMigrationDisplay'
import { useIsProductTrial } from 'Hooks/trial/useIsProductTrial'
import { getProductName } from 'Utils/productName'
import { ScheduleCallClick } from '../ScheduleCallClick'

import {
  AFTER_TRIAL_SUBSCRIPTION_STATUSES,
  TRIAL_STATUSES,
} from 'Consts/afterTrialsubscriptionStatuses'
import { AccountCloseNotification } from './AccountCloseNotification/AccountCloseNotification'
import useMainWrapperStore from 'Features/MainWrapper/stores/mainWrapper'
import { useCanceledAccountNotification } from 'Features/MainWrapper/hooks/useCanceledAccountNotification'

export const GlobalNotification = () => {
  const { t } = useTranslation('general')
  const { setIsTopBannerVisible, isTopBannerVisible } = useMainWrapperStore()
  const { isAdmin } = useUserStore()
  const isProductTrial = useIsProductTrial()
  const { data: planDetails } = useGetPlansDetails()
  const [modalToShow, setModalToShow] = useState<boolean>(false)
  const { isMigratedAccount } = useMigrationDisplay()
  const { cancelledMessage } = useCanceledAccountNotification()

  const handleScheduleCallClick = () =>
    isMigratedAccount || isProductTrial.allowed
      ? window.open(process.env.REACT_APP_CALENDLY_URL, '_blank')
      : setModalToShow(true)

  const getNotificationContent = (): string | ReactNode => {
    const { productsInTrial } = isProductTrial

    // If Agent mode
    if (isAdmin)
      return (
        <>
          <Warning />
          {t('global_notifications.impersonation_mode')}
        </>
      )
    // If Canceled Account or Past Due
    else if (cancelledMessage) return cancelledMessage
    // If Trail per product
    else if (productsInTrial && planDetails?.trial?.endTime) {
      return (
        <>
          {t('global_notifications.trial_days_left_trial_per_product', {
            days: trailDaysLeft(planDetails?.trial?.endTime),
            productName: getProductName(productsInTrial),
          })}
          <ScheduleCallClick
            isOpen={modalToShow}
            handleScheduleCallClick={handleScheduleCallClick}
            setModalToShow={setModalToShow}
          />
        </>
      )
    }
    // If Account Trial
    else if (planDetails?.trial?.endTime && TRIAL_STATUSES.includes(planDetails?.status)) {
      return (
        <>
          {t('global_notifications.trial_days_left', {
            days: trailDaysLeft(planDetails.trial.endTime),
          })}
          <ScheduleCallClick
            isOpen={modalToShow}
            handleScheduleCallClick={handleScheduleCallClick}
            setModalToShow={setModalToShow}
          />
        </>
      )
    } else if (
      !isAdmin &&
      planDetails?.noCreditCard &&
      AFTER_TRIAL_SUBSCRIPTION_STATUSES.includes(planDetails?.status)
    ) {
      return <AccountCloseNotification />
    } else return null
  }

  const notificationContent = useMemo(() => {
    const content = getNotificationContent()
    if (!isTopBannerVisible && content) setIsTopBannerVisible(true)

    return content
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    planDetails,
    isProductTrial,
    isAdmin,
    isMigratedAccount,
    isProductTrial.productsInTrial,
    planDetails?.trial?.endTime,
  ])

  return notificationContent ? (
    <GlobalNotificationStyled>{notificationContent}</GlobalNotificationStyled>
  ) : null
}
